/**
 * Lead-Notification Bell — sticky in the admin header.
 *
 * Responsibilities
 * ----------------
 *  1. Poll `/api/admin/notifications/pulse` every 20 s.
 *  2. On new leads not previously seen: play the selected WebAudio tone,
 *     push a toast (top-right), and record in Notification History.
 *  3. Bell button in header opens a dropdown with unread + history +
 *     tone/enable settings.
 *  4. Deep-link click reuses the AdminDashboard `autoOpen` signal so the
 *     admin lands on the record's detail modal in one tap.
 *
 * Storage (localStorage, keys prefixed `lead_notif_*`):
 *   - lead_notif_enabled  : "1" | "0"
 *   - lead_notif_tone     : "1".."5"   — legacy single-tone (fallback only)
 *   - lead_notif_tone_map : JSON       — per-kind tone map (V137)
 *   - lead_notif_vip_foreign : "1"|"0" — foreign-registration VIP override (V137)
 *   - lead_notif_seen     : JSON.stringify(<lastNSeenIds>) capped at 500
 *   - lead_notif_history  : JSON.stringify(<lastNItems>)   capped at 50
 *   - lead_notif_bootstrap : "1" — first-boot flag so we don't scream
 *                            with 30 tones on initial page load.
 *
 * Tones — 5 short WebAudio patterns. No audio-file assets.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Bell, BellOff, CheckCircle2, Play, Settings, Trash2, User,
    FileText, Briefcase, MessageSquare, Smartphone, ArrowRight, X,
} from "lucide-react";
import { api } from "../../lib/api";

/* ---------------------- Tones (WebAudio oscillator patterns) --------------- */
const TONES = [
    { id: "1", label: "Classic Lead",     notes: [[880, 0.09], [1320, 0.12]] },
    { id: "2", label: "Professional Bell", notes: [[1568, 0.08], [1244, 0.08], [1046, 0.14]] },
    { id: "3", label: "Attention Alert",   notes: [[660, 0.08], [660, 0.08], [990, 0.16]] },
    { id: "4", label: "Soft Business",     notes: [[523, 0.12], [659, 0.14]] },
    { id: "5", label: "Premium Lead",      notes: [[880, 0.08], [1174, 0.08], [1568, 0.10], [2093, 0.14]] },
];

/* Default tone per lead kind — see V137 spec. VIP override (foreign
 * registrations) always plays "5 – Premium Lead" regardless of the map. */
const DEFAULT_TONE_MAP = {
    registration: "1", // Classic Lead
    inquiry:      "4", // Soft Business
    tutor:        "2", // Professional Bell
    app_lead:     "3", // Attention Alert
    invoice:      "5", // Premium Lead
};
const VIP_TONE_ID = "5"; // Premium Lead

let _audioCtx = null;
function _ctx() {
    if (!_audioCtx) {
        try { _audioCtx = new (window.AudioContext || window.webkitAudioContext)(); }
        catch { return null; }
    }
    return _audioCtx;
}

/** Play a tone. `id` is the tone id string; missing → uses first tone. */
export function playTone(id) {
    const ctx = _ctx();
    if (!ctx) return;
    if (ctx.state === "suspended") { try { ctx.resume(); } catch { /**/ } }
    const tone = TONES.find((t) => t.id === id) || TONES[0];
    let t = ctx.currentTime;
    tone.notes.forEach(([freq, dur]) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(freq, t);
        gain.gain.setValueAtTime(0.001, t);
        gain.gain.exponentialRampToValueAtTime(0.32, t + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.001, t + dur);
        osc.connect(gain).connect(ctx.destination);
        osc.start(t); osc.stop(t + dur + 0.05);
        t += dur + 0.02;
    });
}

/* ---------------------- localStorage helpers ---------------------------- */
const LS = {
    getBool: (k, def) => { const v = localStorage.getItem(k); return v == null ? def : v === "1"; },
    setBool: (k, v) => localStorage.setItem(k, v ? "1" : "0"),
    getStr:  (k, def) => localStorage.getItem(k) ?? def,
    setStr:  (k, v) => localStorage.setItem(k, v),
    getJson: (k, def) => { try { return JSON.parse(localStorage.getItem(k)) ?? def; } catch { return def; } },
    setJson: (k, v) => localStorage.setItem(k, JSON.stringify(v)),
};

/** True when the current local time falls inside [start, end) ("HH:MM"); handles overnight ranges. */
export function isWithinQuietHours(start, end, now = new Date()) {
    const toMin = (s) => { const [h, m] = (s || "0:0").split(":").map(Number); return h * 60 + m; };
    const cur = now.getHours() * 60 + now.getMinutes();
    const s = toMin(start), e = toMin(end);
    if (s === e) return false;
    return s < e ? cur >= s && cur < e : cur >= s || cur < e;
}

/* ---------------------- Icon + label per kind --------------------------- */
const KIND_META = {
    registration: { label: "Demo Registration", cls: "bg-blue-100 text-blue-700", Icon: User },
    inquiry:      { label: "Parent Inquiry",    cls: "bg-amber-100 text-amber-700", Icon: MessageSquare },
    tutor:        { label: "Tutor Application", cls: "bg-purple-100 text-purple-700", Icon: Briefcase },
    invoice:      { label: "Invoice",           cls: "bg-emerald-100 text-emerald-700", Icon: FileText },
    app_lead:     { label: "App Lead",          cls: "bg-slate-100 text-slate-700", Icon: Smartphone },
};

const KIND_ORDER = ["registration", "inquiry", "tutor", "app_lead", "invoice"];

/** Resolve tone id for a given lead item, honouring the VIP-foreign
 * override. `map` and `vipForeign` come from user prefs. */
function resolveToneId(item, map, vipForeign) {
    if (vipForeign && item?.kind === "registration" && item?.is_vip) return VIP_TONE_ID;
    return map?.[item?.kind] || DEFAULT_TONE_MAP[item?.kind] || "1";
}

/* ================================================================== */
export default function LeadNotificationBell({ onNavigate }) {
    const [enabled, setEnabled]   = useState(() => LS.getBool("lead_notif_enabled", true));
    const [toneMap, setToneMap]   = useState(() => ({
        ...DEFAULT_TONE_MAP,
        ...(LS.getJson("lead_notif_tone_map", null) || {}),
    }));
    const [vipForeign, setVipForeign] = useState(() => LS.getBool("lead_notif_vip_foreign", true));
    const [quietOn, setQuietOn]       = useState(() => LS.getBool("lead_notif_quiet_enabled", false));
    const [quietStart, setQuietStart] = useState(() => LS.getStr("lead_notif_quiet_start", "22:00"));
    const [quietEnd, setQuietEnd]     = useState(() => LS.getStr("lead_notif_quiet_end", "08:00"));
    const [seenIds, setSeenIds]   = useState(() => new Set(LS.getJson("lead_notif_seen", [])));
    const [history, setHistory]   = useState(() => LS.getJson("lead_notif_history", []));
    const [open, setOpen]         = useState(false);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [toasts, setToasts]     = useState([]);
    const bootstrapped            = useRef(LS.getStr("lead_notif_bootstrap", "0") === "1");
    const wrapRef                 = useRef(null);
    const lastPolledSince         = useRef(null);

    // Persist prefs
    useEffect(() => { LS.setBool("lead_notif_enabled", enabled); }, [enabled]);
    useEffect(() => { LS.setJson("lead_notif_tone_map", toneMap); }, [toneMap]);
    useEffect(() => { LS.setBool("lead_notif_vip_foreign", vipForeign); }, [vipForeign]);
    useEffect(() => { LS.setBool("lead_notif_quiet_enabled", quietOn); }, [quietOn]);
    useEffect(() => { LS.setStr("lead_notif_quiet_start", quietStart); }, [quietStart]);
    useEffect(() => { LS.setStr("lead_notif_quiet_end", quietEnd); }, [quietEnd]);
    useEffect(() => { LS.setJson("lead_notif_history", history.slice(0, 50)); }, [history]);
    useEffect(() => { LS.setJson("lead_notif_seen", Array.from(seenIds).slice(-500)); }, [seenIds]);

    const unreadCount = useMemo(
        () => history.filter((h) => !h.read).length,
        [history],
    );

    /* --------- poll ------------------------------------------------ */
    const poll = useCallback(async () => {
        try {
            const since = lastPolledSince.current;
            const url = since
                ? `/admin/notifications/pulse?since=${encodeURIComponent(since)}`
                : `/admin/notifications/pulse`;
            const { data } = await api.get(url);
            lastPolledSince.current = data.server_now;
            if (!Array.isArray(data.items) || data.items.length === 0) return;

            // First boot ever → mark everything seen so we don't tone-storm
            if (!bootstrapped.current) {
                const bootIds = data.items.map((it) => `${it.kind}:${it.id}`);
                setSeenIds((prev) => { const next = new Set(prev); bootIds.forEach((k) => next.add(k)); return next; });
                setHistory((prev) => {
                    const merged = data.items.map((it) => ({ ...it, key: `${it.kind}:${it.id}`, read: true }));
                    return [...merged, ...prev].slice(0, 50);
                });
                bootstrapped.current = true;
                LS.setStr("lead_notif_bootstrap", "1");
                return;
            }

            // Fresh items — filter unseen
            const fresh = data.items.filter((it) => !seenIds.has(`${it.kind}:${it.id}`));
            if (fresh.length === 0) return;
            const freshKeys = fresh.map((it) => `${it.kind}:${it.id}`);
            setSeenIds((prev) => { const next = new Set(prev); freshKeys.forEach((k) => next.add(k)); return next; });
            setHistory((prev) => {
                const rows = fresh.map((it) => ({ ...it, key: `${it.kind}:${it.id}`, read: false, received_at: new Date().toISOString() }));
                return [...rows, ...prev].slice(0, 50);
            });

            // Ping + toast (only if enabled and outside quiet hours)
            if (enabled && !(quietOn && isWithinQuietHours(quietStart, quietEnd))) {
                // Per-kind tones — stagger up to 3 unique tones by 220ms so
                // rapid bursts of different lead kinds each get heard once.
                try {
                    const playedTones = new Set();
                    let offset = 0;
                    for (const it of fresh) {
                        const tid = resolveToneId(it, toneMap, vipForeign);
                        if (playedTones.has(tid)) continue;
                        playedTones.add(tid);
                        if (offset === 0) {
                            playTone(tid);
                        } else {
                            setTimeout(() => playTone(tid), offset);
                        }
                        offset += 220;
                        if (playedTones.size >= 3) break;
                    }
                } catch { /* AudioContext may need a user gesture first */ }
                // Optional browser notification
                if (typeof Notification !== "undefined" && Notification.permission === "granted") {
                    try {
                        const top = fresh[0];
                        new Notification("🔔 New Lead Received", {
                            body: `New ${KIND_META[top.kind]?.label || top.kind} from ${top.name}`,
                            silent: true,
                        });
                    } catch { /* ignore */ }
                }
            }
            // Toasts (top-right, 6s each)
            setToasts((prev) => [
                ...fresh.slice(0, 3).map((it) => ({ ...it, key: `${it.kind}:${it.id}`, t: Date.now() })),
                ...prev,
            ].slice(0, 5));
        } catch { /* transient — retry next tick */ }
    }, [enabled, toneMap, vipForeign, seenIds, quietOn, quietStart, quietEnd]);

    /* Ask for permission once (best-effort) */
    useEffect(() => {
        if (typeof Notification !== "undefined" && Notification.permission === "default") {
            try { Notification.requestPermission().catch(() => {}); } catch { /* ignore */ }
        }
    }, []);

    /* Poll interval — first tick after 1.5 s, then every 20 s */
    useEffect(() => {
        const t1 = setTimeout(poll, 1500);
        const t2 = setInterval(poll, 20_000);
        return () => { clearTimeout(t1); clearInterval(t2); };
    }, [poll]);

    /* Auto-dismiss toasts after 12 s (mobile banner needs time to be noticed) */
    useEffect(() => {
        if (toasts.length === 0) return;
        const t = setTimeout(() => setToasts((prev) => prev.filter((x) => Date.now() - x.t < 12000)), 1000);
        return () => clearTimeout(t);
    }, [toasts]);

    /* Close dropdown on outside click */
    useEffect(() => {
        const onDoc = (e) => {
            if (!wrapRef.current) return;
            if (!wrapRef.current.contains(e.target)) { setOpen(false); setSettingsOpen(false); }
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    const openItem = (item) => {
        setHistory((prev) => prev.map((h) => (h.key === item.key ? { ...h, read: true } : h)));
        setOpen(false);
        onNavigate?.(item.deep_link_tab, item);
    };

    const markAllRead = () => setHistory((prev) => prev.map((h) => ({ ...h, read: true })));
    const clearHistory = () => setHistory([]);

    return (
        <>
            {/* V173 — Mobile-only full-width new-lead banner.
                Pins below the sticky admin header, slides down with a brief
                pulse, auto-dismisses after 12 s, and lets the admin jump
                to the lead with a single tap. Only renders the single
                most-recent fresh lead so the banner stays legible on 360px
                screens; additional fresh leads still queue in the
                top-right desktop toast list below. */}
            {toasts.length > 0 && (() => {
                const t = toasts[0];
                const meta = KIND_META[t.kind] || KIND_META.registration;
                return (
                    <div
                        className="lg:hidden fixed top-[64px] left-2 right-2 z-[90] animate-[slideDown_240ms_ease-out]"
                        data-testid="lead-notif-mobile-banner"
                        role="status"
                        aria-live="polite"
                    >
                        <div className="bg-white border border-emerald-300 shadow-xl rounded-xl flex items-stretch overflow-hidden ring-2 ring-emerald-200/60">
                            <button
                                type="button"
                                onClick={() => { setToasts((prev) => prev.filter((x) => x.key !== t.key)); openItem(t); }}
                                data-testid={`lead-notif-mobile-banner-cta-${t.kind}-${t.id}`}
                                className="flex-1 min-w-0 text-left px-3 py-2.5 flex items-center gap-2.5 active:bg-emerald-50"
                            >
                                <div className={`shrink-0 w-9 h-9 rounded-lg ${meta.cls} flex items-center justify-center relative`}>
                                    <meta.Icon className="w-4 h-4" />
                                    <span className="absolute -top-0.5 -right-0.5 flex h-2 w-2">
                                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                                        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                                    </span>
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-[0.18em] font-bold text-emerald-700">
                                        🔔 New Lead Received
                                        {t.is_vip && (
                                            <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[9px] tracking-wider" data-testid={`lead-notif-mobile-banner-vip`}>VIP</span>
                                        )}
                                    </div>
                                    <div className="mt-0.5 text-sm font-semibold text-[#0F172A] truncate">{t.name}</div>
                                    <div className="text-[11px] text-slate-500 truncate">
                                        {meta.label}{t.city ? ` · ${t.city}` : ""}{t.phone ? ` · ${t.phone}` : ""}
                                    </div>
                                </div>
                                <div className="shrink-0 inline-flex items-center gap-1 text-[11px] font-bold text-[#1E3A8A] bg-blue-50 px-2 py-1 rounded-md">
                                    Open <ArrowRight className="w-3 h-3" />
                                </div>
                            </button>
                            <button
                                type="button"
                                onClick={() => setToasts((prev) => prev.filter((x) => x.key !== t.key))}
                                data-testid="lead-notif-mobile-banner-dismiss"
                                aria-label="Dismiss notification"
                                className="shrink-0 w-10 border-l border-slate-100 text-slate-400 hover:text-slate-600 hover:bg-slate-50 flex items-center justify-center"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        {/* Queued-lead counter — hints that more leads are stacked behind */}
                        {toasts.length > 1 && (
                            <div
                                data-testid="lead-notif-mobile-banner-queue"
                                className="mt-1 text-center text-[10px] font-bold text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full py-0.5 px-2 inline-flex items-center justify-center mx-auto w-full"
                            >
                                +{toasts.length - 1} more new {toasts.length - 1 === 1 ? "lead" : "leads"} — tap the bell to see all
                            </div>
                        )}
                        <style>{`
                            @keyframes slideDown {
                                from { transform: translateY(-20px); opacity: 0 }
                                to   { transform: translateY(0); opacity: 1 }
                            }
                        `}</style>
                    </div>
                );
            })()}

            {/* Desktop toasts (top-right, above header) — unchanged on >=lg.
                Hidden on mobile so the full-width banner above owns the stage. */}
            <div className="hidden lg:flex fixed top-3 right-3 z-[100] flex-col gap-2 max-w-xs pointer-events-none" data-testid="lead-notif-toasts">
                {toasts.map((t) => {
                    const meta = KIND_META[t.kind] || KIND_META.registration;
                    return (
                        <button key={t.key} type="button"
                            onClick={() => { setToasts((prev) => prev.filter((x) => x.key !== t.key)); openItem(t); }}
                            data-testid={`lead-notif-toast-${t.kind}-${t.id}`}
                            className="pointer-events-auto text-left bg-white border border-slate-200 shadow-lg rounded-xl px-3 py-2.5 hover:border-[#1E3A8A] transition-all">
                            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-bold text-emerald-700">
                                🔔 New Lead Received
                                {t.is_vip && (
                                    <span className="ml-auto px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 tracking-wider" data-testid={`lead-notif-toast-vip-${t.kind}-${t.id}`}>VIP</span>
                                )}
                            </div>
                            <div className="mt-1 flex items-start gap-2">
                                <div className={`shrink-0 w-7 h-7 rounded-lg ${meta.cls} flex items-center justify-center`}>
                                    <meta.Icon className="w-3.5 h-3.5" />
                                </div>
                                <div className="min-w-0">
                                    <div className="text-sm font-semibold text-[#0F172A] truncate">{t.name}</div>
                                    <div className="text-[11px] text-slate-500 truncate">
                                        {meta.label}{t.city ? ` · ${t.city}` : ""}{t.phone ? ` · ${t.phone}` : ""}
                                    </div>
                                </div>
                            </div>
                        </button>
                    );
                })}
            </div>

            {/* Bell + dropdown */}
            <div className="relative" ref={wrapRef} data-testid="lead-notif-wrap">
                <button onClick={() => { setOpen((o) => !o); setSettingsOpen(false); }} data-testid="lead-notif-bell"
                    className="relative inline-flex items-center justify-center w-9 h-9 rounded-lg text-slate-600 hover:text-[#1E3A8A] hover:bg-slate-100">
                    {enabled ? <Bell className="w-5 h-5" /> : <BellOff className="w-5 h-5" />}
                    {unreadCount > 0 && (
                        <span data-testid="lead-notif-badge"
                            className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 text-[9px] font-bold rounded-full bg-rose-500 text-white flex items-center justify-center">
                            {unreadCount > 9 ? "9+" : unreadCount}
                        </span>
                    )}
                </button>

                {open && (
                    <>
                        {/* V146 — Backdrop for reliable outside-click close on touch devices.
                            Transparent, sits under the panel and above the rest of the page. */}
                        <button
                            aria-label="Close notifications"
                            onClick={() => { setOpen(false); setSettingsOpen(false); }}
                            className="fixed inset-0 z-40 bg-transparent sm:hidden"
                            data-testid="lead-notif-backdrop"
                        />
                        <div
                            className={
                                // Mobile (< sm): pin the panel to the top-right corner of the viewport
                                // with a small inset — width auto-scales to any Android screen (320-500 px)
                                // and never extends beyond the left edge.
                                // Desktop / tablet: preserve original `absolute right-0 top-full`
                                // behaviour so the header layout is untouched.
                                "fixed left-2 right-2 top-16 z-50 max-h-[calc(100vh-5rem)] w-auto " +
                                "sm:absolute sm:inset-auto sm:right-0 sm:top-full sm:mt-2 sm:w-[min(22rem,90vw)] sm:max-h-[80vh] " +
                                "bg-white border border-slate-200 rounded-xl shadow-xl flex flex-col overflow-hidden"
                            }
                            data-testid="lead-notif-dropdown"
                        >
                        <div className="px-3 py-2 border-b border-slate-100 flex items-center justify-between">
                            <span className="text-sm font-bold text-[#0F172A]">Lead alerts</span>
                            <div className="flex items-center gap-1">
                                <button onClick={() => setSettingsOpen((s) => !s)} data-testid="lead-notif-settings-toggle"
                                    className="p-1 text-slate-500 hover:text-[#1E3A8A]"><Settings className="w-4 h-4" /></button>
                                {unreadCount > 0 && (
                                    <button onClick={markAllRead} data-testid="lead-notif-mark-all"
                                        className="p-1 text-slate-500 hover:text-emerald-700"><CheckCircle2 className="w-4 h-4" /></button>
                                )}
                                {history.length > 0 && (
                                    <button onClick={clearHistory} data-testid="lead-notif-clear"
                                        className="p-1 text-slate-500 hover:text-rose-600"><Trash2 className="w-4 h-4" /></button>
                                )}
                            </div>
                        </div>

                        {settingsOpen && (
                            <div className="p-3 border-b border-slate-100 bg-slate-50" data-testid="lead-notif-settings">
                                <label className="flex items-center justify-between gap-3 mb-3">
                                    <span className="text-sm font-semibold text-[#0F172A]">Play sound on new lead</span>
                                    <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)}
                                        data-testid="lead-notif-enabled-toggle" className="w-4 h-4 accent-[#1E3A8A]" />
                                </label>

                                <label className="flex items-start justify-between gap-3 mb-3 pb-3 border-b border-slate-200">
                                    <span className="text-sm">
                                        <span className="block font-semibold text-[#0F172A]">VIP tone for foreign registrations</span>
                                        <span className="block text-[11px] text-slate-500 mt-0.5">Any registration with a non-India country plays Premium Lead.</span>
                                    </span>
                                    <input type="checkbox" checked={vipForeign} onChange={(e) => setVipForeign(e.target.checked)}
                                        data-testid="lead-notif-vip-foreign-toggle" className="mt-1 w-4 h-4 accent-[#1E3A8A]" />
                                </label>

                                <div className="mb-3 pb-3 border-b border-slate-200" data-testid="lead-notif-quiet-hours">
                                    <label className="flex items-start justify-between gap-3">
                                        <span className="text-sm">
                                            <span className="block font-semibold text-[#0F172A]">Quiet hours</span>
                                            <span className="block text-[11px] text-slate-500 mt-0.5">Mute alert sounds in this window (alerts still appear).</span>
                                        </span>
                                        <input type="checkbox" checked={quietOn} onChange={(e) => setQuietOn(e.target.checked)}
                                            data-testid="lead-notif-quiet-toggle" className="mt-1 w-4 h-4 accent-[#1E3A8A]" />
                                    </label>
                                    {quietOn && (
                                        <div className="mt-2 flex items-center gap-2 text-xs text-slate-700">
                                            <span>From</span>
                                            <input type="time" value={quietStart} onChange={(e) => setQuietStart(e.target.value || "22:00")}
                                                data-testid="lead-notif-quiet-start" className="border border-slate-200 rounded-md px-1.5 py-1 bg-white focus:border-[#1E3A8A] focus:outline-none" />
                                            <span>to</span>
                                            <input type="time" value={quietEnd} onChange={(e) => setQuietEnd(e.target.value || "08:00")}
                                                data-testid="lead-notif-quiet-end" className="border border-slate-200 rounded-md px-1.5 py-1 bg-white focus:border-[#1E3A8A] focus:outline-none" />
                                            {isWithinQuietHours(quietStart, quietEnd) && (
                                                <span data-testid="lead-notif-quiet-active" className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full">
                                                    <BellOff className="w-3 h-3" /> Active now
                                                </span>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 mb-1.5">Tone per form type</div>
                                <div className="space-y-1.5">
                                    {KIND_ORDER.map((kind) => {
                                        const meta = KIND_META[kind];
                                        const currentId = toneMap[kind] || DEFAULT_TONE_MAP[kind];
                                        return (
                                            <div key={kind} className="flex items-center gap-2">
                                                <div className={`shrink-0 w-6 h-6 rounded-md ${meta.cls} flex items-center justify-center`}>
                                                    <meta.Icon className="w-3 h-3" />
                                                </div>
                                                <span className="flex-1 text-xs font-medium text-slate-700 truncate">{meta.label}</span>
                                                <select
                                                    value={currentId}
                                                    onChange={(e) => setToneMap((prev) => ({ ...prev, [kind]: e.target.value }))}
                                                    data-testid={`lead-notif-tone-select-${kind}`}
                                                    className="text-xs border border-slate-200 rounded-md px-1.5 py-1 bg-white focus:border-[#1E3A8A] focus:outline-none">
                                                    {TONES.map((t) => (
                                                        <option key={t.id} value={t.id}>{t.label}</option>
                                                    ))}
                                                </select>
                                                <button onClick={() => playTone(currentId)}
                                                    data-testid={`lead-notif-tone-preview-${kind}`}
                                                    title={`Preview ${TONES.find((t) => t.id === currentId)?.label || ""}`}
                                                    className="shrink-0 inline-flex items-center justify-center w-6 h-6 text-[#1E3A8A] bg-white border border-slate-200 hover:border-[#1E3A8A] rounded-md">
                                                    <Play className="w-3 h-3" />
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>

                                <div className="mt-3 pt-3 border-t border-slate-200">
                                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-slate-500 mb-1.5">Preview all tones</div>
                                    <div className="flex flex-wrap gap-1.5">
                                        {TONES.map((tone) => (
                                            <button key={tone.id} onClick={() => playTone(tone.id)}
                                                data-testid={`lead-notif-tone-lib-preview-${tone.id}`}
                                                className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#1E3A8A] bg-white border border-slate-200 hover:border-[#1E3A8A] px-2 py-1 rounded-md">
                                                <Play className="w-3 h-3" /> {tone.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="overflow-y-auto flex-1">
                            {history.length === 0 ? (
                                <div className="px-4 py-10 text-center">
                                    <Bell className="w-6 h-6 mx-auto text-slate-300 mb-2" />
                                    <p className="text-sm text-slate-500">No lead alerts yet. New form submissions will appear here.</p>
                                </div>
                            ) : (
                                history.map((it) => {
                                    const meta = KIND_META[it.kind] || KIND_META.registration;
                                    return (
                                        <button key={it.key} type="button" onClick={() => openItem(it)}
                                            data-testid={`lead-notif-history-${it.kind}-${it.id}`}
                                            className={`w-full text-left px-3 py-2.5 border-b border-slate-100 last:border-0 hover:bg-slate-50 flex items-start gap-2 ${it.read ? "" : "bg-blue-50/40"}`}>
                                            <div className={`shrink-0 w-8 h-8 rounded-lg ${meta.cls} flex items-center justify-center`}>
                                                <meta.Icon className="w-4 h-4" />
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-sm font-semibold text-[#0F172A] truncate">{it.name}</span>
                                                    {it.is_vip && (
                                                        <span className="px-1.5 py-0.5 rounded-full text-[9px] font-bold bg-amber-100 text-amber-800 tracking-wider" data-testid={`lead-notif-history-vip-${it.kind}-${it.id}`}>VIP</span>
                                                    )}
                                                    {!it.read && <span className="w-1.5 h-1.5 rounded-full bg-rose-500" />}
                                                </div>
                                                <div className="text-[11px] text-slate-500 truncate">
                                                    {meta.label}{it.city ? ` · ${it.city}` : ""}{it.phone ? ` · ${it.phone}` : ""}
                                                </div>
                                                <div className="text-[10px] text-slate-400 mt-0.5">
                                                    {formatWhen(it.created_at)}
                                                </div>
                                            </div>
                                        </button>
                                    );
                                })
                            )}
                        </div>
                    </div>
                    </>
                )}
            </div>
        </>
    );
}

function formatWhen(iso) {
    if (!iso) return "just now";
    const then = new Date(iso).getTime();
    const secs = Math.max(1, Math.floor((Date.now() - then) / 1000));
    if (secs < 60) return `${secs}s ago`;
    if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
    if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
    return new Date(iso).toLocaleString();
}
